import { appEnv } from './env'
import { setBridgeSupabaseAccessToken, clearBridgeSupabaseAccessToken } from './supabase-auth-token'

/**
 * Cliente do `spa-bridge` do SSO Flexibase (padrão `web-spa-bridge-v1`), no
 * molde do Hub SISTEMAS.
 *
 * O bridge é um sidecar que fala OIDC com o SSO e guarda a sessão num cookie
 * HttpOnly (`portal_sso_bridge_session`). O browser nunca vê refresh token:
 * só o que `GET /api/session` devolve, incluindo o JWT que autoriza o
 * PostgREST (guardado em memória — supabase-auth-token.ts).
 *
 * Tudo same-origin: o nginx do app (produção) e o proxy do Vite (dev) expõem
 * `/auth/*` e `/api/session` sob o mesmo host — sem isso o cookie não viaja e
 * o login entra em loop.
 */

export interface BridgeSession {
  readonly userId: string
  readonly email: string
  readonly displayName: string
  readonly supabaseAccessToken: string | null
}

/**
 * Guarda de logout: entre o clique em "sair" e a navegação, o app pode
 * renderizar de novo e ver `anonimo`. Sem esta flag o gate dispararia um login
 * novo — o clássico "não consigo sair".
 */
let signingOut = false

export function getIsSigningOut(): boolean {
  return signingOut
}

function isJwtLike(value: unknown): value is string {
  return typeof value === 'string' && value.split('.').length === 3
}

function firstString(...candidates: unknown[]): string | null {
  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim()) return candidate.trim()
  }
  return null
}

/** O formato do payload variou entre gerações do bridge; aceita as conhecidas. */
export function extractSupabaseAccessToken(payload: Record<string, unknown>): string | null {
  const session = (payload.session ?? payload) as Record<string, unknown>
  const metadata = (session.metadata ?? {}) as Record<string, unknown>
  const candidate = firstString(
    session.supabaseAccessToken,
    session.supabase_access_token,
    metadata.supabaseAccessToken,
    metadata.supabase_access_token,
  )
  return isJwtLike(candidate) ? candidate : null
}

function extractUser(payload: Record<string, unknown>) {
  const session = (payload.session ?? payload) as Record<string, unknown>
  const user = (session.user ?? session) as Record<string, unknown>
  return {
    userId: firstString(user.userId, user.user_id, user.sub, user.id),
    email: firstString(user.email) ?? '',
    displayName: firstString(user.name, user.displayName, user.full_name, user.email) ?? '',
  }
}

/**
 * Lê a sessão corrente. `null` = anônimo.
 *
 * Qualquer resposta que não seja JSON de sessão conta como anônimo: com a borda
 * desalinhada, `/api/session` cai no fallback da SPA e devolve o `index.html`.
 */
export async function fetchBridgeSession(): Promise<BridgeSession | null> {
  let response: Response
  try {
    response = await fetch(`${appEnv.bridgeBaseUrl}/api/session`, {
      credentials: 'include',
      cache: 'no-store',
      headers: { accept: 'application/json' },
    })
  } catch {
    return null
  }
  if (!response.ok) return null
  if (!response.headers.get('content-type')?.includes('application/json')) return null

  let payload: Record<string, unknown>
  try {
    payload = (await response.json()) as Record<string, unknown>
  } catch {
    return null
  }
  if (payload.authenticated === false) return null

  const { userId, email, displayName } = extractUser(payload)
  if (!userId) return null

  const token = setBridgeSupabaseAccessToken(extractSupabaseAccessToken(payload)) ?? null
  return { userId, email, displayName, supabaseAccessToken: token }
}

export function buildBridgeLoginUrl(returnTo: string = window.location.href): string {
  const params = new URLSearchParams({ appId: appEnv.appId, returnTo })
  return `${appEnv.bridgeBaseUrl}/auth/login?${params.toString()}`
}

export function redirectToBridgeLogin(returnTo?: string): void {
  window.location.assign(buildBridgeLoginUrl(returnTo))
}

/**
 * Encerra a sessão no bridge e devolve a URL de logout do SSO (a navegação
 * para ela derruba a sessão global; sair só do app deixaria o próximo login
 * passar direto).
 */
export async function signOutFromBridge(): Promise<string | null> {
  signingOut = true
  clearBridgeSupabaseAccessToken()
  try {
    const response = await fetch(`${appEnv.bridgeBaseUrl}/auth/logout`, {
      method: 'POST',
      credentials: 'include',
      cache: 'no-store',
      headers: { accept: 'application/json' },
    })
    if (!response.ok) return null
    const payload = (await response.json()) as { logoutUrl?: string }
    return firstString(payload.logoutUrl)
  } catch {
    return null
  }
}
